import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Sparkles } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { api, errMsg, fmtDateTime } from "../../lib/api";
import { QUAL_STATUSES, QUAL_CHECKS } from "../../lib/constants";
import { useAuth } from "../../lib/auth";

const Card = ({ title, children, testId }) => (
  <div className="rounded-lg border bg-white p-5 shadow-sm" data-testid={testId}>
    <h3 className="mb-3 text-sm font-bold uppercase tracking-wide text-slate-700">{title}</h3>
    {children}
  </div>
);

const Qualification = ({ loc, patch }) => {
  const checks = loc.qualification_checks || {};
  return (
    <Card title="Sales qualification" testId="lead-qualification-card">
      <Select value={loc.qualification_status || "Not contacted"} onValueChange={(v) => patch({ qualification_status: v })}>
        <SelectTrigger data-testid="qualification-select"><SelectValue /></SelectTrigger>
        <SelectContent>{QUAL_STATUSES.map((s) => <SelectItem key={s} value={s} data-testid={`qualification-option-${s.replace(/\s+/g, "-").toLowerCase()}`}>{s}</SelectItem>)}</SelectContent>
      </Select>
      <p className="mt-2 text-[11px] text-slate-500">Qualified requires all four confirmations below. Automatic research never marks a lead Qualified. Opportunity priority is separate.</p>
      <div className="mt-3 space-y-2">
        {QUAL_CHECKS.map(([k, l]) => (
          <label key={k} className="flex items-center gap-2 text-sm"><Checkbox checked={!!checks[k]} onCheckedChange={(v) => patch({ qualification_checks: { [k]: !!v } })} data-testid={`qual-check-${k}`} />{l}</label>
        ))}
      </div>
    </Card>
  );
};

const NextAction = ({ loc, patch, team, isAdmin }) => {
  const [na, setNa] = useState(loc.next_action || "");
  const [fu, setFu] = useState(loc.follow_up_date || "");
  return (
    <Card title="Assignment & next action" testId="lead-next-action-card">
      <div className="space-y-3">
        <div className="space-y-1">
          <Label>Assigned salesperson</Label>
          {isAdmin ? (
            <Select value={loc.assigned_to || ""} onValueChange={(v) => patch({ assigned_to: v })}>
              <SelectTrigger data-testid="assign-select"><SelectValue placeholder="Unassigned" /></SelectTrigger>
              <SelectContent>{team.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} ({u.role})</SelectItem>)}</SelectContent>
            </Select>
          ) : <div className="text-sm" data-testid="assigned-name">{team.find((u) => u.id === loc.assigned_to)?.name || "You"}</div>}
        </div>
        <div className="space-y-1"><Label>Next action</Label><Input value={na} onChange={(e) => setNa(e.target.value)} placeholder="e.g. Call owner to schedule discovery" data-testid="next-action-input" /></div>
        <div className="space-y-1"><Label>Follow-up date</Label><Input type="date" value={fu} onChange={(e) => setFu(e.target.value)} data-testid="follow-up-date-input" /></div>
        <Button size="sm" onClick={() => patch({ next_action: na, follow_up_date: fu })} className="bg-slate-900" data-testid="next-action-save-btn">Save</Button>
        <label className="flex items-center gap-2 border-t pt-3 text-sm font-semibold text-red-700">
          <Checkbox checked={!!loc.do_not_contact} onCheckedChange={(v) => patch({ do_not_contact: !!v })} data-testid="lead-dnc-checkbox" />Do not contact this restaurant
        </label>
        <label className="flex items-center gap-2 text-sm font-semibold text-blue-800">
          <Checkbox checked={!!loc.existing_customer} onCheckedChange={(v) => patch({ existing_customer: !!v })} data-testid="lead-existing-customer-checkbox" />Existing 360RGE customer
        </label>
      </div>
    </Card>
  );
};

const Notes = ({ loc, reload }) => {
  const [text, setText] = useState("");
  const add = async () => {
    try {
      await api.post(`/locations/${loc.id}/notes`, { text });
      setText("");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Card title="Internal notes" testId="lead-notes-card">
      <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Internal only — excluded from prospect reports" data-testid="note-input" />
      <Button size="sm" className="mt-2 bg-slate-900" disabled={!text.trim()} onClick={add} data-testid="note-add-btn">Add note</Button>
      <ul className="mt-4 space-y-3">
        {[...(loc.notes || [])].reverse().map((n) => (
          <li key={n.id} className="border-l-2 border-orange-300 pl-3 text-sm" data-testid="note-item"><div className="whitespace-pre-wrap">{n.text}</div><div className="text-[11px] text-slate-500">{n.author} · {fmtDateTime(n.created_at)}</div></li>
        ))}
      </ul>
    </Card>
  );
};

const Outreach = ({ loc, reload }) => {
  const [busy, setBusy] = useState(false);
  const gen = async () => {
    setBusy(true);
    try {
      await api.post(`/locations/${loc.id}/outreach-draft`);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const d = loc.outreach_draft;
  return (
    <Card title="Outreach draft (never sent automatically)" testId="lead-outreach-card">
      <Button size="sm" variant="outline" onClick={gen} disabled={busy} data-testid="outreach-generate-btn"><Sparkles className="mr-1 h-3.5 w-3.5" />{busy ? "Drafting…" : d ? "Regenerate draft" : "Generate draft from sourced findings"}</Button>
      {d && (
        <div className="mt-3">
          <pre className="whitespace-pre-wrap rounded-md bg-slate-50 p-3 font-sans text-sm" data-testid="outreach-draft-text">{d.text}</pre>
          <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(d.text); toast.success("Copied"); }} data-testid="outreach-copy-btn"><Copy className="mr-1 h-3.5 w-3.5" />Copy</Button>
        </div>
      )}
    </Card>
  );
};

export default function LeadPanel({ loc, reload }) {
  const { user } = useAuth();
  const [team, setTeam] = useState([]);
  useEffect(() => {
    api.get("/team").then((r) => setTeam(r.data)).catch(() => {});
  }, []);
  const patch = async (body) => {
    try {
      await api.patch(`/locations/${loc.id}`, body);
      toast.success("Lead updated");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <Qualification loc={loc} patch={patch} />
      <NextAction key={loc.updated_at} loc={loc} patch={patch} team={team} isAdmin={user?.role === "admin"} />
      <Notes loc={loc} reload={reload} />
      <Outreach loc={loc} reload={reload} />
    </div>
  );
}
